import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';

const testStripeConnection = async () => {
  try {
    console.log('Testing Stripe connection...');
    console.log('Stripe key:', process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY?.substring(0, 12) + '...');

    const publishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
    if (!publishableKey) {
      return { success: false, message: 'Stripe publishable key is not configured' };
    }

    const stripe = await loadStripe(publishableKey);
    console.log('Stripe loaded successfully:', !!stripe);
    
    if (stripe) {
      console.log('Stripe instance created successfully');
      return { success: true, message: 'Stripe connection successful' };
    } else {
      console.error('Stripe instance is null');
      return { success: false, message: 'Stripe instance is null' };
    }
  } catch (error) {
    console.error('Stripe connection failed:', error);
    return { success: false, message: `Stripe connection failed: ${error.message}` };
  }
};

const OnboardingDevHelper = () => {
  const [leadId, setLeadId] = useState('');
  const [apiData, setApiData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [stripeTestResult, setStripeTestResult] = React.useState(null);
  const [testingStripe, setTestingStripe] = React.useState(false);

  // Get the correct API base URL
  const getApiBaseUrl = () => {
    if (process.env.NODE_ENV === 'development') {
      return process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
    } else {
      return process.env.REACT_APP_API_URL || '/api';
    }
  };

  const steps = [
    { id: 1, title: 'Complete Setup', description: 'Clinic details and account creation' },
    { id: 2, title: 'Connect Pabau', description: 'Pabau API key configuration' },
    { id: 3, title: 'Start Trial', description: 'Payment setup and trial activation (AccountSetupFlow)' }
  ];

  const componentSteps = [
    { name: 'BasicInformationComplete', path: '/onboarding?step=1&dev_mode=true' },
    { name: 'PabauConfiguration', path: '/onboarding?step=2&dev_mode=true' },
    { name: 'AccountSetupFlow (Payment)', path: '/onboarding?step=3&dev_mode=true' },

    { name: 'AccountSetupFlow (Completed)', path: '/onboarding?step=3&dev_mode=true&flow=completed' },
    { name: 'EmailVerificationStep', path: '/onboarding?step=email_verification&dev_mode=true' }
  ];

  const setupTestSteps = [
    { name: 'Payment Pending', path: '/onboarding?step=3&dev_mode=true&flow=payment_pending', description: 'Initial payment form' },
    { name: 'Processing', path: '/onboarding?step=3&dev_mode=true&flow=processing', description: 'Account creation progress' },
    { name: 'Completed', path: '/onboarding?step=3&dev_mode=true&flow=completed', description: 'Success state with dashboard redirect' }
  ];



  const fetchLeadData = async () => {
    if (!leadId) return;
    
    setIsLoading(true);
    try {
      const response = await fetch(`${getApiBaseUrl()}/onboarding/lead/${leadId}`);
      const data = await response.json();
      setApiData(data);
    } catch (error) {
      console.error('Error fetching lead data:', error);
      setApiData({ error: 'Failed to fetch lead data' });
    } finally {
      setIsLoading(false);
    }
  };

  const getStepFromStatus = (status) => {
    const statusMap = {
      'email_sent': 'email_verification',
      'profile_completed': 1,
      'plan_selected': 2,
      'pabau_configured': 3,
      'payment_pending': 3,
      'payment_completed': 'success',
      'onboarding_complete': 'success'
    };
    return statusMap[status] || 1;
  };

  const handleStripeTest = async () => {
    setTestingStripe(true);
    const result = await testStripeConnection();
    setStripeTestResult(result);
    setTestingStripe(false);
  };

  return (
    <div style={{ padding: '40px', maxWidth: '1000px', margin: '0 auto' }}>
      <h1>Onboarding Development Helper</h1>
      
      {/* Account Setup Flow Testing Section */}
      <div style={{ marginBottom: '40px', padding: '20px', background: '#f0f9ff', border: '1px solid #0ea5e9', borderRadius: '8px' }}>
        <h2 style={{ margin: '0 0 16px 0', color: '#0369a1' }}>🔗 Test AccountSetupFlow Phases</h2>
        <p style={{ margin: '0 0 16px 0', color: '#075985' }}>
          Test the payment setup and trial activation flow with different phases:
        </p>
        <div style={{ display: 'grid', gap: '12px', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))' }}>
          {setupTestSteps.map(step => (
            <Link
              key={step.name}
              to={step.path}
              style={{
                display: 'block',
                padding: '12px 16px',
                background: '#dbeafe',
                border: '1px solid #3b82f6',
                borderRadius: '6px',
                textDecoration: 'none',
                color: '#1e40af',
                fontSize: '14px'
              }}
            >
              <div style={{ fontWeight: '600', marginBottom: '4px' }}>{step.name}</div>
              <div style={{ fontSize: '12px', color: '#3730a3' }}>{step.description}</div>
            </Link>
          ))}
        </div>
      </div>



      {/* Specific Lead Debugging Section */}
      <div style={{ marginBottom: '40px', padding: '20px', background: '#fff7ed', border: '1px solid #fb923c', borderRadius: '8px' }}>
        <h2 style={{ margin: '0 0 16px 0', color: '#ea580c' }}>🔍 Debug Specific Lead</h2>
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: '8px', color: '#9a3412', fontWeight: '500' }}>
            Lead ID:
          </label>
          <input
            type="text"
            value={leadId}
            onChange={(e) => setLeadId(e.target.value)}
            placeholder="Enter lead ID (e.g., 8683e7d6-4912-4854-8be3-99bf37123c73)"
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #fed7aa',
              borderRadius: '6px',
              fontSize: '14px',
              fontFamily: 'monospace'
            }}
          />
        </div>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '16px' }}>
          <button
            onClick={fetchLeadData}
            disabled={isLoading || !leadId}
            style={{
              padding: '8px 16px',
              background: '#ea580c',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              cursor: isLoading || !leadId ? 'not-allowed' : 'pointer',
              opacity: isLoading || !leadId ? 0.6 : 1
            }}
          >
            {isLoading ? 'Loading...' : 'Fetch Lead Data'}
          </button>
          {leadId && (
            <Link
              to={`/onboarding?lead_id=${leadId}&step=${apiData?.success ? getStepFromStatus(apiData.lead.status) : 1}`}
              style={{
                padding: '8px 16px',
                background: '#16a34a',
                color: 'white',
                textDecoration: 'none',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            >
              Jump to Lead's Current Step
            </Link>
          )}
        </div>
        
        {apiData && (
          <div style={{ marginTop: '16px' }}>
            {apiData.success ? (
              <div style={{ padding: '12px', background: '#f0f9ff', border: '1px solid #0ea5e9', borderRadius: '6px' }}>
                <h4 style={{ margin: '0 0 8px 0', color: '#0369a1' }}>Lead Data:</h4>
                <div style={{ fontSize: '14px', color: '#075985', fontFamily: 'monospace' }}>
                  <div><strong>Email:</strong> {apiData.lead.email}</div>
                  <div><strong>Clinic:</strong> {apiData.lead.clinic_name}</div>
                  <div><strong>Status:</strong> {apiData.lead.status}</div>
                  <div><strong>Plan:</strong> {apiData.lead.selected_plan} ({apiData.lead.billing_period})</div>
                  <div><strong>Customer ID:</strong> {apiData.lead.stripe_customer_id}</div>
                  <div><strong>Created:</strong> {new Date(apiData.lead.created_at).toLocaleString()}</div>
                </div>
              </div>
            ) : (
              <div style={{ padding: '12px', background: '#fef2f2', border: '1px solid #ef4444', borderRadius: '6px' }}>
                <p style={{ margin: '0', color: '#dc2626' }}>
                  {apiData.error || 'Lead not found'}
                </p>
              </div>
            )}
          </div>
        )}
        
        {leadId && (
          <div style={{ marginTop: '16px' }}>
            <h4 style={{ margin: '0 0 8px 0', color: '#ea580c' }}>Quick Links for This Lead:</h4>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <Link
                to={`/onboarding?lead_id=${leadId}&step=1`}
                style={{
                  padding: '6px 12px',
                  background: '#fbbf24',
                  color: '#92400e',
                  textDecoration: 'none',
                  borderRadius: '4px',
                  fontSize: '12px'
                }}
              >
                Step 1 (Profile)
              </Link>
              <Link
                to={`/onboarding?lead_id=${leadId}&step=2`}
                style={{
                  padding: '6px 12px',
                  background: '#fbbf24',
                  color: '#92400e',
                  textDecoration: 'none',
                  borderRadius: '4px',
                  fontSize: '12px'
                }}
              >
                Step 2 (Pabau)
              </Link>
              <Link
                to={`/onboarding?lead_id=${leadId}&step=3`}
                style={{
                  padding: '6px 12px',
                  background: '#fbbf24',
                  color: '#92400e',
                  textDecoration: 'none',
                  borderRadius: '4px',
                  fontSize: '12px'
                }}
              >
                Step 3 (Payment)
              </Link>

            </div>
          </div>
        )}
      </div>

      <div style={{ marginBottom: '40px' }}>
        <h2>📋 Quick Links to Onboarding Steps</h2>
        <div style={{ display: 'grid', gap: '16px' }}>
          {steps.map(step => (
            <Link
              key={step.id}
              to={`/onboarding?step=${step.id}&dev_mode=true`}
              style={{
                display: 'block',
                padding: '16px',
                background: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                textDecoration: 'none',
                color: '#1e293b',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#f1f5f9';
                e.target.style.borderColor = '#cbd5e1';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = '#f8fafc';
                e.target.style.borderColor = '#e2e8f0';
              }}
            >
              <div style={{ fontSize: '18px', fontWeight: '600', marginBottom: '4px' }}>
                Step {step.id}: {step.title}
              </div>
              <div style={{ fontSize: '14px', color: '#64748b' }}>
                {step.description}
              </div>
            </Link>
          ))}
        </div>
      </div>

      <div style={{ marginBottom: '40px' }}>
        <h2>🧩 All Components</h2>
        <div style={{ display: 'grid', gap: '12px' }}>
          {componentSteps.map(component => (
            <Link
              key={component.name}
              to={component.path}
              style={{
                display: 'block',
                padding: '12px 16px',
                background: '#fef3c7',
                border: '1px solid #fbbf24',
                borderRadius: '6px',
                textDecoration: 'none',
                color: '#92400e',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              {component.name}
            </Link>
          ))}
        </div>
      </div>

      <div style={{ marginBottom: '40px' }}>
        <h2>💡 Development Tips</h2>
        <ul style={{ color: '#64748b', lineHeight: '1.6' }}>
          <li>All links use <code>dev_mode=true</code> which provides mock data</li>
          
          <li>Use <code>&flow=completed</code> to test the success state</li>
          <li>You can jump between steps freely in development mode</li>
          <li>No API calls are made in development mode</li>
          <li>Use the browser's back/forward buttons to navigate</li>
          <li>Add <code>&dev_mode=true</code> to any onboarding URL for development mode</li>
          <li><strong>Use the "Debug Specific Lead" section above to work with real lead data</strong></li>
        </ul>
      </div>

      <div style={{ padding: '20px', background: '#f0f9ff', border: '1px solid #0ea5e9', borderRadius: '8px' }}>
        <h3 style={{ margin: '0 0 12px 0', color: '#0369a1' }}>🚀 Regular Flow</h3>
        <p style={{ margin: '0', color: '#075985' }}>
          To test the actual flow, start at{' '}
          <Link to="/onboarding" style={{ color: '#0369a1' }}>/onboarding</Link>
          {' '}without dev_mode parameter.
        </p>
      </div>

      <div style={{ marginBottom: '40px', padding: '20px', background: '#f0f9ff', border: '1px solid #0ea5e9', borderRadius: '8px' }}>
        <h2 style={{ margin: '0 0 16px 0', color: '#0369a1' }}>🔗 Stripe Connection Test</h2>
        <p style={{ margin: '0 0 16px 0', color: '#075985' }}>
          Test the connection to Stripe:
        </p>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '16px' }}>
          <button
            onClick={handleStripeTest}
            disabled={testingStripe}
            style={{
              padding: '8px 16px',
              background: '#ea580c',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              cursor: testingStripe ? 'not-allowed' : 'pointer',
              opacity: testingStripe ? 0.6 : 1
            }}
          >
            {testingStripe ? 'Testing...' : 'Test Stripe Connection'}
          </button>
          {stripeTestResult && (
            <div style={{ padding: '12px', background: stripeTestResult.success ? '#d1fae5' : '#fef2f2', border: stripeTestResult.success ? '1px solid #22c55e' : '1px solid #ef4444', borderRadius: '6px' }}>
              <p style={{ margin: '0', color: stripeTestResult.success ? '#15803d' : '#dc2626' }}>
                {stripeTestResult.message}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OnboardingDevHelper; 
